import axios, { AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  Product, 
  ProductsResponse, 
  Order, 
  OrdersResponse, 
  QuoteRequest, 
  RequestsResponse,
  LoginCredentials,
  CreateOrderData,
  CreateQuoteRequestData,
  CreateProductData,
  Admin
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('API Request with token:', config.url, token ? 'Token present' : 'No token');
    } else {
      console.log('API Request without token:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse<ApiResponse<{ token: string; admin: Admin }>>> =>
    api.post('/auth/login', credentials),
  
  getMe: (): Promise<AxiosResponse<ApiResponse<{ admin: Admin }>>> =>
    api.get('/auth/me'),
  
  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.put('/auth/change-password', data),
};

// Products API
export const productsAPI = {
  getProducts: (params?: {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
    featured?: boolean;
    sort?: string;
  }): Promise<AxiosResponse<ApiResponse<ProductsResponse>>> =>
    api.get('/products', { params }),
  
  getProduct: (id: string): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
    api.get(`/products/${id}`),
  
  getCategories: (): Promise<AxiosResponse<ApiResponse<{ categories: string[] }>>> =>
    api.get('/products/categories/list'),
  
  createProduct: (data: CreateProductData): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
    api.post('/products', data),
  
  updateProduct: (id: string, data: Partial<CreateProductData>): Promise<AxiosResponse<ApiResponse<{ product: Product }>>> =>
    api.put(`/products/${id}`, data),
  
  deleteProduct: (id: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete(`/products/${id}`),
};

// Orders API
export const ordersAPI = {
  createOrder: (data: CreateOrderData): Promise<AxiosResponse<ApiResponse<{ order: Order }>>> =>
    api.post('/orders', data),
  
  getOrders: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    sort?: string;
  }): Promise<AxiosResponse<ApiResponse<OrdersResponse>>> =>
    api.get('/orders', { params }),
  
  getOrder: (id: string): Promise<AxiosResponse<ApiResponse<{ order: Order }>>> =>
    api.get(`/orders/${id}`),
  
  updateOrderStatus: (id: string, data: { status: string; adminNotes?: string }): Promise<AxiosResponse<ApiResponse<{ order: Order }>>> =>
    api.put(`/orders/${id}/status`, data),
  
  getOrderStats: (): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/orders/stats/summary'),
};

// Quote Requests API
export const requestsAPI = {
  createRequest: (data: CreateQuoteRequestData): Promise<AxiosResponse<ApiResponse<{ request: QuoteRequest }>>> =>
    api.post('/requests', data),
  
  getRequests: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    sort?: string;
  }): Promise<AxiosResponse<ApiResponse<RequestsResponse>>> =>
    api.get('/requests', { params }),
  
  getRequest: (id: string): Promise<AxiosResponse<ApiResponse<{ request: QuoteRequest }>>> =>
    api.get(`/requests/${id}`),
  
  updateRequest: (id: string, data: { status?: string; adminNotes?: string }): Promise<AxiosResponse<ApiResponse<{ request: QuoteRequest }>>> =>
    api.put(`/requests/${id}`, data),
  
  deleteRequest: (id: string): Promise<AxiosResponse<ApiResponse<null>>> =>
    api.delete(`/requests/${id}`),
  
  getRequestStats: (): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get('/requests/stats/summary'),
};

// File upload API
export const uploadAPI = {
  uploadProofOfPayment: (file: File): Promise<AxiosResponse<ApiResponse<{ url: string }>>> => {
    const formData = new FormData();
    formData.append('proofOfPayment', file);
    return api.post('/upload/pop', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  uploadProductImages: (files: File[]): Promise<AxiosResponse<ApiResponse<{ urls: string[] }>>> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });
    return api.post('/upload/products', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

export default api;
